import React from "react";
import { twMerge } from "tailwind-merge";
import ThreeDotLoader from "./ThreeDotLoader";

/** Filled / outline control sizes (min tap target). */
const CONTROL_SIZES = {
  sm: "px-3 py-1.5 text-[13px] min-h-[36px]",
  md: "px-5 py-3 text-[15px] min-h-[44px]",
  lg: "px-6 py-3.5 text-[15px] min-h-[48px]",
  /** Friendly aliases */
  small: "px-3 py-1.5 text-[13px] min-h-[36px]",
  medium: "px-5 py-3 text-[15px] min-h-[44px]",
  large: "px-6 py-3.5 text-[15px] min-h-[48px]",
};

/** Text-link sizes — no fill padding / min-height. */
const LINK_SIZES = {
  sm: "text-[13px]",
  md: "text-[14px]",
  lg: "text-[15px]",
  small: "text-[13px]",
  medium: "text-[14px]",
  large: "text-[15px]",
};

export const BUTTON_VARIANTS = {
  /** Primary CTA — uses --host-cta inside themed shells, else charcoal */
  primary:
    "bg-primary text-[color:var(--host-cta-fg,#ffffff)] border border-transparent hover:brightness-[1.08] active:scale-[0.99] host-cta-btn",
  /** Alias used by older ReadableNavButton */
  accent:
    "bg-primary text-[color:var(--host-cta-fg,#ffffff)] border border-transparent hover:brightness-[1.08] active:scale-[0.99] host-cta-btn",
  night:
    "bg-night text-white border border-night hover:brightness-110 active:brightness-95",
  secondary:
    "bg-surface text-ink border border-subtle hover:border-ink/20 hover:bg-canvas",
  ghost:
    "bg-transparent text-ink/70 border border-transparent hover:text-ink hover:bg-ink/[0.04]",
  /** Tertiary underline CTA — pairs with TextLink */
  link: "bg-transparent border-0 text-ink/55 underline underline-offset-4 decoration-ink/20 hover:text-ink hover:decoration-ink/40 p-0 min-h-0 h-auto",
  danger:
    "bg-red-600 text-white border border-red-600 hover:bg-red-700 active:bg-red-700",
  dangerOutline:
    "bg-surface text-red-700 border border-red-200 hover:bg-red-50 active:bg-red-50",
  /** On night heroes */
  glass:
    "bg-white/15 backdrop-blur-sm border border-white/30 text-white hover:bg-white/25 active:bg-white/30",
  /** Outline on dark hero (Home secondary CTA) */
  heroSecondary:
    "bg-transparent border border-white/30 text-white/95 hover:bg-white/10 hover:border-white/50",
};

const VARIANTS = BUTTON_VARIANTS;

/** Light-surface variants get dark dots; dark-surface variants get light dots. */
const LIGHT_SURFACE_VARIANTS = new Set(["secondary", "ghost", "link", "dangerOutline"]);

/**
 * Quiet product button — Home / QuietMarketing language.
 *
 * Variants: primary | secondary | ghost | link | night | danger | dangerOutline | glass | heroSecondary
 * Sizes: sm | md | lg (aliases: small | medium | large)
 *
 * `loading` is opt-in: use it only when the button is blocked waiting on an
 * async gate (e.g. class RSVP until eligibility loads). Most buttons should
 * keep their label and use `disabled` / copy like "Saving…" instead.
 */
export default function Button({
  children,
  variant = "primary",
  size = "md",
  type = "button",
  className = "",
  as: Tag = "button",
  compact = false,
  disabled,
  loading = false,
  loadingLabel = "Loading",
  ...rest
}) {
  const isDisabled = Boolean(disabled || loading);
  const buttonProps = Tag === "button" ? { type, disabled: isDisabled } : {};
  const resolvedVariant = VARIANTS[variant] ? variant : "primary";
  const isLink = resolvedVariant === "link";
  const sizeClass = isLink
    ? LINK_SIZES[size] || LINK_SIZES.md
    : compact
      ? "h-9 min-h-9 px-3 text-[13px]"
      : CONTROL_SIZES[size] || CONTROL_SIZES.md;
  const loaderTone = LIGHT_SURFACE_VARIANTS.has(resolvedVariant) ? "onLight" : "onDark";

  return (
    <Tag
      {...buttonProps}
      aria-busy={loading || undefined}
      className={twMerge(
        "inline-flex items-center justify-center gap-2 rounded-btn font-ui font-medium whitespace-nowrap transition-[filter,transform,background-color,border-color,color,text-decoration-color] duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-ink/30 focus-visible:outline-offset-2 disabled:cursor-not-allowed",
        // Keep full opacity while loading; only fade for a real disabled state
        loading ? "disabled:opacity-100" : "disabled:opacity-50",
        sizeClass,
        VARIANTS[resolvedVariant],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <ThreeDotLoader alt={loadingLabel} tone={loaderTone} />
      ) : (
        children
      )}
    </Tag>
  );
}

/** @deprecated Prefer Button variant="primary" size="lg" */
export function QuietPrimaryButton(props) {
  return <Button variant="primary" size="lg" {...props} />;
}

/** @deprecated Prefer Button variant="secondary" size="lg" */
export function QuietSecondaryButton(props) {
  return <Button variant="secondary" size="lg" {...props} />;
}
